import type { ListingStatus } from "@loot-os/fulfillment-core/listing/types";
import { VENDOR_IMAGE_SWEEP_MAX_RESENDS } from "@loot-os/fulfillment-core/vendor-image/limits";
import { useEffect, useState } from "react";
import { formatRelativeTime } from "../lib/format";
import { ListingStatusBadge } from "./badges";
import { ProcessImageAgainButton } from "./ProcessImageAgainButton";

/** How often the ages are worked out again while the page stays open. */
const AGE_REFRESH_MS = 60_000;

/**
 * Lists the StockX listings that wait for a processed image, oldest write first. Shoppers do not
 * see an IMAGE_PROCESSING row, and tenants read it as INACTIVE; an ACTIVE row is on sale and shows
 * a copy of an older photo. Each row's age comes from its last write, which every refresh moves,
 * so it is a lower bound on the wait.
 */
export function AwaitingProcessedImageAlert({
  total,
  listings,
}: {
  total: number;
  listings: {
    id: string;
    title: string;
    status: ListingStatus;
    updatedAt: string;
    acceptsImageJob: boolean;
    imageJobResends: number;
    processingRefusal: string | null;
  }[];
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), AGE_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm">
      <p>
        {total} StockX {total === 1 ? "listing waits" : "listings wait"} for a processed image.
        Shoppers do not see an IMAGE PROCESSING row. An ACTIVE row is on sale and shows an older
        photo.
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {listings.map((listing) => (
          <li key={listing.id} className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col">
              <span className="flex items-center gap-2">
                {listing.title}
                <ListingStatusBadge status={listing.status} />
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {listing.id} · last written {formatRelativeTime(listing.updatedAt, now)}, so waiting
                at least that long
              </span>
              {listing.processingRefusal !== null && (
                <span className="text-xs text-muted-foreground">
                  Processing refused this photo ({listing.processingRefusal.replaceAll("_", " ")}).
                  The hourly sweep does not resend it. Process image again tries once more, and a
                  new StockX photo clears it.
                </span>
              )}
              {listing.processingRefusal === null && listing.imageJobResends > 0 && (
                <span className="text-xs text-muted-foreground">
                  {listing.imageJobResends >= VENDOR_IMAGE_SWEEP_MAX_RESENDS
                    ? `The hourly sweep resent its image job ${listing.imageJobResends} times and stopped. Process image again restarts the count.`
                    : `The hourly sweep resent its image job ${listing.imageJobResends} of ${VENDOR_IMAGE_SWEEP_MAX_RESENDS} times.`}
                </span>
              )}
            </div>
            {listing.acceptsImageJob ? (
              <ProcessImageAgainButton listingId={listing.id} />
            ) : (
              <span className="text-xs text-muted-foreground">
                No image job: the row shows neither its StockX photo nor a stored copy
              </span>
            )}
          </li>
        ))}
        {total > listings.length && (
          <li className="text-xs text-muted-foreground">and {total - listings.length} more</li>
        )}
      </ul>
    </div>
  );
}
